import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, LockKeyhole, User } from 'lucide-react';
import { login } from '../../api/endpoints';
import { ApiError, setToken } from '../../api/client';
import { useSeo } from '../../lib/seo';
import '../../styles/admin.css';

export default function Login() {
  useSeo({ title: '管理登录', description: '铁头功的博客后台登录。' });
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/admin/dashboard';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const username = String(form.get('username') ?? '').trim();
    const password = String(form.get('password') ?? '');

    if (!username || !password) {
      setFormError('请输入用户名与密码');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const result = await login(username, password);
      setToken(result.accessToken);
      navigate(from, { replace: true });
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : '登录失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit} noValidate>
        <div className="login-brand">
          <img src="/favicon.png" alt="" aria-hidden="true" />
        </div>
        <h1 className="login-title">管理后台登录</h1>
        <p className="login-subtitle">请使用管理员账号登录以管理站点内容</p>

        <div className="form-field" style={{ marginBottom: '1rem' }}>
          <label className="form-label" htmlFor="username">
            用户名
          </label>
          <div style={{ position: 'relative' }}>
            <User
              size={16}
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-ink-faint)',
              }}
            />
            <input
              id="username"
              name="username"
              className="form-input"
              style={{ paddingLeft: 38 }}
              autoComplete="username"
              maxLength={50}
              required
            />
          </div>
        </div>

        <div className="form-field" style={{ marginBottom: '1.25rem' }}>
          <label className="form-label" htmlFor="password">
            密码
          </label>
          <div style={{ position: 'relative' }}>
            <LockKeyhole
              size={16}
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-ink-faint)',
              }}
            />
            <input
              id="password"
              name="password"
              type="password"
              className="form-input"
              style={{ paddingLeft: 38 }}
              autoComplete="current-password"
              maxLength={100}
              required
            />
          </div>
        </div>

        {formError && (
          <div className="inline-alert inline-alert-error" style={{ marginBottom: '1rem' }}>
            {formError}
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          {submitting && <Loader2 size={15} className="spinner-sm" aria-hidden="true" />}
          {submitting ? '登录中…' : '登 录'}
        </button>
      </form>
    </div>
  );
}
